import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import PayrollStatsCards from './components/PayrollStatsCards';
import EmployeeDataTable from './components/EmployeeDataTable';
import PayrollChartsPanel from './components/PayrollChartsPanel';
import QuickActionsPanel from './components/QuickActionsPanel';
import EnforcementBanner from '../../components/billing/EnforcementBanner';
import { Button, EmptyState, Note, SkeletonPage, usePageTitle, useCompany } from '../../components/app2';
import { authService } from '../../utils/authService';
import { strapiService } from '../../utils/api';

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [profileData, setProfileData] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Periodos de nómina REALES de la empresa. El panel mostraba métricas
  // calculadas sobre campos que el backend nunca envía (`receipt_status`,
  // `last_payroll_date`), así que decía "no hay nóminas procesadas" y 0% de
  // completitud aunque la empresa tuviera miles de recibos.
  const [periodos, setPeriodos] = useState([]);

  // Get current user data
  const currentUser = authService?.getCurrentUser();

  // La empresa la manda el contexto del shell (URL > última elegida > primera del
  // perfil), no el `state` de la navegación: así entrar por el menú lateral
  // muestra la MISMA empresa que traías, en vez de caer siempre en la primera.
  // El `state` se sigue aceptando como atajo cuando llegas desde la ficha de una
  // empresa, porque ahí ya viene el objeto completo y evita una espera.
  const { idEmpresaActiva } = useCompany();
  const navigationState = location?.state;
  const navigationCompany = navigationState?.company;
  const companyIdFromState = navigationState?.companyId;
  const companyIdFromUrl = searchParams.get('companyId');

  const targetCompanyId = companyIdFromUrl || companyIdFromState || idEmpresaActiva;

  useEffect(() => {
    const loadProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Loading company profile data...', {
          hasNavigationCompany: !!navigationCompany,
          companyIdFromState: companyIdFromState,
          companyIdFromUrl: companyIdFromUrl,
          companyName: navigationCompany?.businessName || navigationCompany?.name
        });

        // FIXED: Use navigation company first if available, then find by companyId
        let selectedCompany = null;

        // El objeto que viene por navegación solo sirve si es la empresa activa:
        // si cambiaste de empresa con el selector, este atajo está caduco.
        const navigationSirve = navigationCompany
          && (!targetCompanyId || Number(navigationCompany?.id) === Number(targetCompanyId));

        if (navigationSirve) {
          selectedCompany = navigationCompany;
          console.log('✅ Using company from navigation state:', navigationCompany?.businessName || navigationCompany?.name);
        } else {
          // Fetch user profile with companies and employees
          const profile = await authService?.getUserProfile();

          if (profile?.user && profile?.companies) {
            setProfileData(profile);

            // If companyId available (from state or URL), find that specific company
            if (targetCompanyId) {
              selectedCompany = profile?.companies?.find(c => c.id == targetCompanyId);
              console.log('✅ Using company from companyId:', targetCompanyId, selectedCompany?.name);
            }

            // Fallback to first company if not found by ID
            if (!selectedCompany) {
              selectedCompany = profile?.companies?.[0];
              console.log('⚠️ Using first company from profile:', selectedCompany?.name);
            }
          } else {
            // Fallback to stored user data if profile fetch fails
            console.log('⚠️ Using stored user data as fallback');
            const userData = authService?.getCurrentUser();
            if (userData?.companies?.length > 0) {
              if (targetCompanyId) {
                selectedCompany = userData?.companies?.find(c => c.id == targetCompanyId);
              }
              if (!selectedCompany) {
                selectedCompany = userData?.companies?.[0];
              }
            }
          }
        }

        setCurrentCompany(selectedCompany);

        // Periodos de nómina reales: alimentan la gráfica mensual y la cobertura.
        const idEmpresa = selectedCompany?.id || selectedCompany?.company_id;
        if (idEmpresa) {
          strapiService.payroll.getCompanyReceiptPeriods(idEmpresa)
            .then((r) => setPeriodos(Array.isArray(r?.data) ? r.data : []))
            .catch((e) => console.error('No se pudieron cargar los periodos de nómina:', e));
        }

        console.log('✅ Company data loaded:', {
          companyName: selectedCompany?.businessName || selectedCompany?.company_name,
          companyRfc: selectedCompany?.rfc,
          employeesCount: selectedCompany?.employees?.length || 0
        });
      } catch (error) {
        console.error('❌ Error loading profile data:', error);
        setError('Error al cargar los datos del perfil');

        // Use navigation company or stored user data as fallback
        if (navigationCompany) {
          setCurrentCompany(navigationCompany);
        } else {
          const userData = authService?.getCurrentUser();
          if (userData?.companies?.length > 0) {
            setCurrentCompany(userData?.companies?.[0]);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [navigationCompany, targetCompanyId]);

  const handleLogout = () => {
    try {
      // Clear user session from localStorage
      localStorage.removeItem('userSession');
      
      // Clear any other session-related data if exists
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      
      // Add slight delay to ensure localStorage is cleared
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
      
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, still navigate to login
      navigate('/login', { replace: true });
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (profileData?.user?.first_name && profileData?.user?.last_name) {
      return `${profileData?.user?.first_name} ${profileData?.user?.last_name}`;
    }
    if (profileData?.user?.first_name) {
      return profileData?.user?.first_name;
    }
    return currentUser?.profile?.firstName || currentUser?.username || 'Usuario';
  };

  // Get company display name
  const getCompanyDisplayName = () => {
    return currentCompany?.businessName || currentCompany?.company_name || currentCompany?.name || 'Empresa';
  };

  // El header del shell nombra la empresa que se está viendo. Importa sobre todo
  // cuando un super admin entra al panel de UNA empresa concreta: antes ese dato
  // vivía en un <h1> dentro del contenido. Mientras carga se deja el título de
  // la ruta ("Panel").
  usePageTitle({ title: currentCompany ? getCompanyDisplayName() : null });

  if (loading) {
    return <SkeletonPage kpis={4} rows={6} />;
  }

  if (error && !currentCompany) {
    return (
      <EmptyState
        icon="error"
        size="lg"
        title="No se pudo cargar el panel"
        text={error}
        action={
          <Button variant="outline" icon="refresh" onClick={() => window.location?.reload()}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <div className="a2-anim-screen">
      {/* Aviso de límite de plan (solo cuando hay sobrecupo) */}
      <div className="a2-slot"><EnforcementBanner /></div>

      {/* Aviso no bloqueante: el panel cargó pero algún dato viene incompleto */}
      {error ? (
        <Note tone="warn" style={{ marginBottom: 16 }}>
          Algunos datos pueden no estar actualizados. {error}
        </Note>
      ) : null}

      <PayrollStatsCards companyData={currentCompany} periodos={periodos} />

      {/* La tabla de colaboradores va a ancho completo: son ocho columnas y en
          una columna de 1.7fr quedaban cortadas. El diseño la pone al lado del
          panel lateral, pero ahí su lista tiene cinco campos, no ocho. */}
      <div style={{ marginBottom: 16 }}>
        <EmployeeDataTable companyData={currentCompany} />
      </div>

      <div className="a2-grid-2-wide">
        <PayrollChartsPanel companyData={currentCompany} periodos={periodos} variant="detail" />

        {/* Columna lateral: nómina mensual + resumen + acciones */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <PayrollChartsPanel companyData={currentCompany} periodos={periodos} variant="monthly" />
          <QuickActionsPanel companyData={currentCompany} periodos={periodos} />
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;